import {Store} from 'redux';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {ConversationModel} from '../models/api/conversationModel';
import {PrecogModel, PrecogTypesEnum} from '../models/api/precogModel';
import {AppState} from '../store/appState';
import {Dispatch, StateGetter} from '../store/appStore';
import {ThunkAction} from '../store/appThunk';
import {ResourceMap} from '../store/appTypes';
import {
  ConversationPrecogId,
  deleteConversationPrecog,
  precogExpirationDelays,
} from '../store/data/precogs/conversationPrecogsActions';
import {postAsync} from './http/http';

/*
 * Precog expirations.
 */

const precogExpirations: {[conversationId: number]: {[teammateId: number]: number | undefined}} = {};
let previousConversationPrecogs: ResourceMap<ReadonlyArray<PrecogModel>> = {};

/*
 * Setup.
 */

export function setupPrecogExpirations(store: Store<AppState>) {
  store.subscribe(() => {
    const {conversationPrecogs} = store.getState().data;

    if (conversationPrecogs === previousConversationPrecogs) {
      return;
    }

    // Reset expiration of precogs.
    Object.keys(conversationPrecogs)
      .map(Number)
      .forEach((conversationId: number) => {
        const precogs = conversationPrecogs[conversationId];
        if (!precogs) {
          return;
        }

        const previousPrecogs = previousConversationPrecogs[conversationId] || [];

        // We select precogs that are new or updated and set expirations.
        precogs
          .filter((precog) => {
            const previousPrecog = previousPrecogs.find((p) => p.teammateId === precog.teammateId);

            return !isSamePrecog(previousPrecog, precog);
          })
          .forEach((precog) => {
            const {teammateId} = precog;

            const expirations = precogExpirations[conversationId];
            if (expirations) {
              const expiration = expirations[teammateId];

              if (expiration) {
                clearTimeout(expiration);
              }
            }

            // We need to remove precogs from the store after a delay.
            const delay = precogExpirationDelays[precog.type];
            precogExpirations[conversationId] = {
              ...expirations,
              [teammateId]: globalWindow.setInstrumentedTimeout(
                () => maybeRemoveExpiredPrecog(store.dispatch, store.getState, {conversationId, teammateId}),
                delay,
              ),
            };
          });
      });

    previousConversationPrecogs = store.getState().data.conversationPrecogs;
  });
}

function isSamePrecog(existingPrecog: PrecogModel | undefined, newPrecog: PrecogModel) {
  return (
    existingPrecog &&
    existingPrecog.teammateId === newPrecog.teammateId &&
    existingPrecog.conversationId === newPrecog.conversationId &&
    existingPrecog.updatedAt === newPrecog.updatedAt &&
    existingPrecog.type === newPrecog.type &&
    existingPrecog.status === newPrecog.status
  );
}

function getExistingPrecog(
  getState: StateGetter,
  conversationId: number,
  teammateId: number,
): PrecogModel | undefined {
  const conversationPrecogs = getState().data.conversationPrecogs[conversationId];
  if (!conversationPrecogs) {
    return undefined;
  }

  return conversationPrecogs.find((p) => p.teammateId === teammateId);
}

/** ::setupPrecogExpirations only updates precog expirations for new/updated precogs.
 * It doesn't do anything for deleted precogs hence why there is a special function here in order to delete precogs
 * taking care of precog expirations as well.
 */
export function deletePrecog(precogId: ConversationPrecogId, precogType?: PrecogTypesEnum): ThunkAction {
  return (dispatch, getState) => {
    const {conversationId, teammateId} = precogId;

    // Clean the expirations map.
    const expirations = precogExpirations[conversationId];
    const expiration = expirations && expirations[teammateId];
    if (expiration) {
      clearTimeout(expiration);
      delete expirations[teammateId];
    }

    if (expirations && Object.keys(expirations).length === 0) {
      delete precogExpirations[conversationId];
    }

    return dispatch(deleteConversationPrecog(precogId, precogType));
  };
}

function maybeRemoveExpiredPrecog(dispatch: Dispatch, getState: StateGetter, precogId: ConversationPrecogId) {
  // Make sure that our precog is still in the store.
  const existingPrecog = getExistingPrecog(getState, precogId.conversationId, precogId.teammateId);
  if (!existingPrecog) {
    return;
  }

  // If so, remove it.
  dispatch(deletePrecog(precogId));
}

export async function stopPrecogAsync(conversation: ConversationModel) {
  const url = `${conversation.url}/precog/stop`;
  return postAsync(url);
}
